/**
 * 团队页。
 *
 * 我的团队信息（计费模式/余额）+ 成员列表 + 邀请成员 +
 * 调整成员额度/角色 + 切换计费模式（shared/independent）。
 * 未创建团队时展示创建团队入口。
 *
 * @module pages/TeamPage
 */
import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';

import PageHeader from '@/components/common/PageHeader';
import StatCard from '@/components/common/StatCard';
import MoneyText from '@/components/common/MoneyText';
import DataTable, { type Column } from '@/components/common/DataTable';
import { useToast } from '@/hooks/useToast';
import { useApi } from '@/hooks/useApi';
import {
  createTeam,
  getMyTeam,
  inviteMember,
  updateMember,
  updateBillingMode,
} from '@/services/team';
import type { BillingMode, TeamMember, TeamRole } from '@/types';

/** 计费模式元信息。 */
const BILLING_LABEL: Record<BillingMode, string> = {
  shared: '共享余额',
  independent: '独立额度',
};

/** 成员角色标签。 */
const ROLE_LABEL: Record<TeamRole, string> = {
  admin: '管理员',
  member: '成员',
};

/**
 * 团队页组件。
 *
 * @returns 页面元素。
 */
export default function TeamPage(): JSX.Element {
  const { data: team, loading, refetch } = useApi(() => getMyTeam());
  const { show, Toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<{
    name: string;
    billingMode: BillingMode;
  }>({ name: '', billingMode: 'shared' });
  const [creating, setCreating] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<{
    userId: string;
    role: TeamRole;
    quotaLimit: string;
  }>({ userId: '', role: 'member', quotaLimit: '' });
  const [inviting, setInviting] = useState(false);

  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [editForm, setEditForm] = useState<{ quotaLimit: string; role: TeamRole }>(
    { quotaLimit: '', role: 'member' },
  );
  const [savingMember, setSavingMember] = useState(false);
  const [modeBusy, setModeBusy] = useState(false);

  const members: TeamMember[] = team?.members ?? [];

  const handleCreateTeam = async (): Promise<void> => {
    if (!createForm.name.trim()) {
      show('请填写团队名称', 'warning');
      return;
    }
    setCreating(true);
    try {
      await createTeam(createForm);
      show('团队创建成功');
      setCreateOpen(false);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '创建失败', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (): Promise<void> => {
    if (!team) return;
    const userId = Number(inviteForm.userId);
    if (!Number.isFinite(userId) || userId <= 0) {
      show('请填写有效的用户 ID', 'warning');
      return;
    }
    setInviting(true);
    try {
      await inviteMember(team.id, {
        userId,
        role: inviteForm.role,
        quotaLimit: inviteForm.quotaLimit ? inviteForm.quotaLimit : null,
      });
      show('已发送邀请');
      setInviteOpen(false);
      setInviteForm({ userId: '', role: 'member', quotaLimit: '' });
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '邀请失败', 'error');
    } finally {
      setInviting(false);
    }
  };

  const openEdit = (member: TeamMember): void => {
    setEditMember(member);
    setEditForm({ quotaLimit: member.quotaLimit, role: member.role });
  };

  const handleSaveMember = async (): Promise<void> => {
    if (!team || !editMember) return;
    setSavingMember(true);
    try {
      await updateMember(team.id, editMember.id, {
        quotaLimit: editForm.quotaLimit ? editForm.quotaLimit : null,
        role: editForm.role,
      });
      show('成员信息已更新');
      setEditMember(null);
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '更新失败', 'error');
    } finally {
      setSavingMember(false);
    }
  };

  const handleModeChange = async (
    _e: React.MouseEvent<unknown>,
    val: BillingMode | null,
  ): Promise<void> => {
    if (!team || !val || val === team.billingMode) return;
    setModeBusy(true);
    try {
      await updateBillingMode(team.id, val);
      show('计费模式已切换');
      await refetch();
    } catch (err) {
      show(err instanceof Error ? err.message : '切换失败', 'error');
    } finally {
      setModeBusy(false);
    }
  };

  // 未加入/未创建团队：展示创建入口。
  if (!loading && !team) {
    return (
      <Box>
        <PageHeader title="团队" subtitle="创建团队，与成员共享或独立管理额度" />
        <Card>
          <CardContent>
            <Typography variant="body1" gutterBottom>
              你还没有加入任何团队。
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              创建团队
            </Button>
          </CardContent>
        </Card>

        <Dialog
          open={createOpen}
          onClose={() => !creating && setCreateOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>创建团队</DialogTitle>
          <DialogContent>
            <TextField
              label="团队名称"
              fullWidth
              margin="normal"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            />
            <TextField
              label="计费模式"
              select
              fullWidth
              margin="normal"
              value={createForm.billingMode}
              onChange={(e) =>
                setCreateForm({
                  ...createForm,
                  billingMode: e.target.value as BillingMode,
                })
              }
            >
              <MenuItem value="shared">共享余额</MenuItem>
              <MenuItem value="independent">独立额度</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={creating}>
              取消
            </Button>
            <Button onClick={handleCreateTeam} variant="contained" disabled={creating}>
              {creating ? '创建中...' : '创建'}
            </Button>
          </DialogActions>
        </Dialog>
        {Toast}
      </Box>
    );
  }

  const memberColumns: Column<TeamMember>[] = [
    { key: 'userId', header: '用户 ID', render: (m) => `#${m.userId}` },
    {
      key: 'role',
      header: '角色',
      render: (m) => ROLE_LABEL[m.role],
    },
    {
      key: 'balance',
      header: '余额',
      align: 'right',
      render: (m) => <MoneyText value={m.balance} />,
    },
    {
      key: 'quotaLimit',
      header: '额度上限',
      align: 'right',
      render: (m) =>
        m.quotaLimit ? (
          <MoneyText value={m.quotaLimit} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            不限
          </Typography>
        ),
    },
    {
      key: 'actions',
      header: '操作',
      align: 'right',
      render: (m) => (
        <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(m)}>
          调整
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="团队"
        subtitle="管理团队计费模式与成员"
        action={
          team ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              邀请成员
            </Button>
          ) : undefined
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard title="团队名称" value={team?.name ?? '-'} />
        <StatCard
          title="团队余额"
          value={team ? <MoneyText value={team.balance} /> : '-'}
          color="#4f46e5"
        />
        <StatCard
          title="计费模式"
          value={team ? BILLING_LABEL[team.billingMode] : '-'}
          color="#16a34a"
        />
      </Box>

      {team && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              计费模式
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              共享余额：成员共用团队余额；独立额度：成员各自独立限额。
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={team.billingMode}
              exclusive
              disabled={modeBusy}
              onChange={handleModeChange}
            >
              <ToggleButton value="shared">共享余额</ToggleButton>
              <ToggleButton value="independent">独立额度</ToggleButton>
            </ToggleButtonGroup>
          </CardContent>
        </Card>
      )}

      <Typography variant="h6" sx={{ mb: 1.5 }}>
        成员列表
      </Typography>
      <DataTable
        columns={memberColumns}
        rows={members}
        rowKey={(m) => m.id}
        loading={loading}
        emptyText="暂无成员"
      />

      {/* 邀请成员对话框 */}
      <Dialog
        open={inviteOpen}
        onClose={() => !inviting && setInviteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>邀请成员</DialogTitle>
        <DialogContent>
          <TextField
            label="用户 ID"
            type="number"
            fullWidth
            margin="normal"
            value={inviteForm.userId}
            onChange={(e) => setInviteForm({ ...inviteForm, userId: e.target.value })}
          />
          <TextField
            label="角色"
            select
            fullWidth
            margin="normal"
            value={inviteForm.role}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, role: e.target.value as TeamRole })
            }
          >
            <MenuItem value="member">成员</MenuItem>
            <MenuItem value="admin">管理员</MenuItem>
          </TextField>
          <TextField
            label="额度上限（元，独立额度模式，留空不限）"
            fullWidth
            margin="normal"
            value={inviteForm.quotaLimit}
            onChange={(e) =>
              setInviteForm({ ...inviteForm, quotaLimit: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviting}>
            取消
          </Button>
          <Button onClick={handleInvite} variant="contained" disabled={inviting}>
            {inviting ? '邀请中...' : '邀请'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 编辑成员对话框 */}
      <Dialog
        open={Boolean(editMember)}
        onClose={() => !savingMember && setEditMember(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>调整成员</DialogTitle>
        <DialogContent>
          <TextField
            label="角色"
            select
            fullWidth
            margin="normal"
            value={editForm.role}
            onChange={(e) =>
              setEditForm({ ...editForm, role: e.target.value as TeamRole })
            }
          >
            <MenuItem value="member">成员</MenuItem>
            <MenuItem value="admin">管理员</MenuItem>
          </TextField>
          <TextField
            label="额度上限（元，留空不限）"
            fullWidth
            margin="normal"
            value={editForm.quotaLimit}
            onChange={(e) =>
              setEditForm({ ...editForm, quotaLimit: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMember(null)} disabled={savingMember}>
            取消
          </Button>
          <Button onClick={handleSaveMember} variant="contained" disabled={savingMember}>
            {savingMember ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {Toast}
    </Box>
  );
}
